import { useCallback, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import {
  Accordion,
  AccordionContent,
  AccordionTitle,
  Button,
  Divider,
  Form,
  Icon,
  Label,
  Message,
} from "semantic-ui-react";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import type { OpenAPI } from "simplydo/interfaces";
import { Input } from "simplydo/react/component-library";
import { CheckboxHeader } from "simplydo/react/components/CheckboxHeader";
import { RadioCard } from "simplydo/react/components/RadioCard";
import { RichTextViewer } from "simplydo/react/richtext";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";

import { formatOption } from "./statementUtils";

type Challenge = OpenAPI.GET<"/challenges/{id}">["response"];
type Impact =
  OpenAPI.GET<"/organisations/{id}/impacts">["response"]["impacts"][number];
type ImpactCategory =
  OpenAPI.GET<"/organisations/{id}/impacts">["response"]["impactCategories"][number];
type ImpactStatement =
  OpenAPI.GET<"/ideas/{idea_id}/impactStatement">["response"]["impactStatements"]["userEstimate"];

type MappedImpactCategory = ImpactCategory & {
  impacts?: Impact[];
  visible?: boolean;
};

type ImpactStatementProps = {
  impactStatement: ImpactStatement;
  previousStatement: ImpactStatement;
  statementDefinition: Challenge["impactStatements"];
  updateImpactStatement: (statement: ImpactStatement) => void;
  stage: OpenAPI.Schemas["ImpactStatement"]["stage"];
  editable?: boolean;
  rangeInputType?: "auto" | "allowCustom";
  createImpactStatement: (statement: ImpactStatement) => void;
  isCreating?: boolean;
};

const ImpactStatementView = ({
  impactStatement,
  previousStatement,
  updateImpactStatement,
  statementDefinition,
  stage,
  editable,
  rangeInputType,
  isCreating,
  createImpactStatement,
}: ImpactStatementProps) => {
  const { t } = useTranslation();
  const [impactList, setImpactList] = useState<Array<Impact>>([]);
  const [impactCategories, setImpactCategories] = useState<
    Array<ImpactCategory>
  >([]);
  const user = useAppSelector((state) => state.user);

  const [mappedImpactCategories, setMappedImpactCategories] = useState<
    MappedImpactCategory[]
  >([]);
  useEffect(() => {
    setMappedImpactCategories(
      [
        {
          _id: undefined,
          name: "Default category",
          order: -1,
        } as MappedImpactCategory,
      ]
        .concat(impactCategories)
        .map((category) => {
          const impacts = impactList
            .filter((i) => i.category?.id === category._id)
            .sort(
              (a, b) => (a.category?.order || 0) - (b.category?.order || 0),
            );
          return { ...category, impacts };
        })
        .sort((a, b) => a.order - b.order),
    );
  }, [impactCategories, impactList]);

  const setImpactStatement = useCallback(
    (setter) => {
      const finalValue = setter(impactStatement);

      updateImpactStatement(finalValue);
    },
    [impactStatement, updateImpactStatement],
  );

  const updateSingleImpact = useCallback(
    (impactId: string, setter) => {
      setImpactStatement((prev) => {
        const newImpact = setter(prev?.impacts?.[impactId] ?? {});
        return {
          ...(prev ?? {}),
          impacts: {
            ...(prev?.impacts ?? {}),
            [impactId]: { ...(prev?.impacts?.[impactId] ?? {}), ...newImpact },
          },
        };
      });
    },
    [setImpactStatement],
  );

  useEffect(() => {
    let isMounted = true;
    if (user && user.ownerOrganisation._id) {
      api.organisations.getImpacts(
        user.ownerOrganisation._id,
        ({ impacts, impactCategories }) => {
          if (!isMounted) return;
          setImpactList(impacts);
          setImpactCategories(impactCategories);
        },
        () => {},
      );
    }

    return () => {
      isMounted = false;
    };
  }, [user]);
  if (editable && !impactStatement) {
    return (
      <div key={stage}>
        {editable &&
          statementDefinition?.helpText &&
          stage in statementDefinition.helpText &&
          statementDefinition.helpText[stage] && (
            <RichTextViewer value={statementDefinition?.helpText[stage]} />
          )}

        <Divider section />
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Button
            secondary
            loading={isCreating}
            onClick={() => {
              createImpactStatement({ impacts: {}, description: "" });
            }}
          >
            <Icon name="plus" />
            Begin {t("generic.impact")} statement
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div key={stage}>
      {editable &&
        statementDefinition?.helpText &&
        stage in statementDefinition.helpText &&
        statementDefinition.helpText[stage] && (
          <RichTextViewer value={statementDefinition?.helpText[stage]} />
        )}
      <Divider hidden />
      <Accordion styled fluid>
        {mappedImpactCategories.map((group) => {
          const enabledImpacts = group?.impacts?.filter(
            (impact) =>
              statementDefinition?.impacts?.[impact._id]?.enabled &&
              !statementDefinition?.impacts?.[impact._id]?.hiddenFrom?.includes(
                stage,
              ),
          );
          if (!enabledImpacts || enabledImpacts.length === 0) {
            return null;
          }

          const chosenImpacts = enabledImpacts.filter(
            (impact) => impactStatement?.impacts?.[impact._id]?.enabled,
          );

          return (
            <>
              <AccordionTitle
                tabIndex={0}
                active={group.visible}
                onKeyDown={handleClickForKeyboard(() => {
                  setMappedImpactCategories((prev) =>
                    prev.map((g) =>
                      g._id === group._id ? { ...g, visible: !g.visible } : g,
                    ),
                  );
                })}
                onClick={() => {
                  setMappedImpactCategories((prev) =>
                    prev.map((g) =>
                      g._id === group._id ? { ...g, visible: !g.visible } : g,
                    ),
                  );
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Icon name="dropdown" />
                    {group.name}
                  </div>
                  <Label
                    size="small"
                    color={chosenImpacts.length > 0 ? "blue" : "grey"}
                  >
                    {chosenImpacts?.length || 0} / {enabledImpacts?.length || 0}{" "}
                    {util.pluralise(
                      chosenImpacts?.length || 0,
                      `${t("generic.impact")}`,
                      `${t("generic.impacts")}`,
                      false,
                    )}{" "}
                    selected
                  </Label>
                </div>
              </AccordionTitle>
              <AccordionContent active={group.visible}>
                {enabledImpacts.map((impact, index) => {
                  return (
                    <>
                      {index !== 0 ?
                        <Divider style={{ marginTop: 8, marginBottom: 8 }} />
                      : null}
                      <div key={impact._id}>
                        <CheckboxHeader
                          disabled={!editable}
                          style={{ marginBottom: 0 }}
                          header={
                            <div>
                              {impact.icon ?
                                <Icon name={impact.icon} />
                              : null}
                              {impact.name}
                              {impact?.unit && <span> ({impact.unit})</span>}
                            </div>
                          }
                          description={
                            <div>
                              {
                                <span style={{ whiteSpace: "pre-line" }}>
                                  {impact.description}
                                </span>
                              }
                              {previousStatement ?
                                <div style={{ marginTop: 15 }}>
                                  <strong>Previous estimate:</strong> <br />
                                  {(
                                    previousStatement.impacts[impact._id]
                                      ?.enabled
                                  ) ?
                                    <>
                                      <UserChip
                                        user={
                                          previousStatement.ownerCreatedBy as OpenAPI.Schemas["User"]
                                        }
                                        compact="very"
                                      />
                                      {(
                                        previousStatement.impacts[impact._id]
                                          .choice?.value
                                      ) ?
                                        ` estimated "${previousStatement.impacts[impact._id].choice?.value}"`
                                      : null}
                                      {(
                                        previousStatement.impacts[impact._id]
                                          .frequency
                                      ) ?
                                        ` ${previousStatement.impacts[impact._id].frequency}`
                                      : null}
                                      .
                                      <br />
                                      {(
                                        previousStatement.impacts[impact._id]
                                          .justification
                                      ) ?
                                        `Their comment: "${previousStatement.impacts[impact._id].justification}"`
                                      : null}
                                    </>
                                  : <>
                                      <UserChip
                                        user={
                                          previousStatement.ownerCreatedBy as OpenAPI.Schemas["User"]
                                        }
                                        compact="very"
                                      />
                                      {` did not indicate this ${t("generic.impact")}.`}
                                    </>
                                  }
                                </div>
                              : null}
                            </div>
                          }
                          as="h5"
                          checked={
                            impactStatement?.impacts?.[impact._id]?.enabled
                          }
                          onChange={() => {
                            updateSingleImpact(impact._id, (prev) => ({
                              ...prev,
                              enabled: !prev?.enabled,
                            }));
                          }}
                          willHideChildren
                        >
                          <div style={{ marginTop: 15, marginBottom: 12 }}>
                            {(
                              statementDefinition.impacts[impact._id].options ||
                              statementDefinition.impacts[impact._id]
                                .customOptionEnabled
                            ) ?
                              <RadioCard.Group
                                editable={editable}
                                defaultValue={
                                  (
                                    impactStatement?.impacts?.[impact._id]
                                      ?.choice?.custom
                                  ) ?
                                    "__custom"
                                  : impactStatement?.impacts?.[impact._id]
                                      ?.choice?.id
                                }
                                onValueChange={(value) => {
                                  updateSingleImpact(impact._id, (prev) => ({
                                    ...prev,
                                    choice:
                                      value === "__custom" ?
                                        { custom: true }
                                      : { id: value },
                                  }));
                                }}
                              >
                                {statementDefinition.impacts[
                                  impact._id
                                ].options?.map((option) => {
                                  const optionValue = formatOption(
                                    impact,
                                    option,
                                  );
                                  return (
                                    <RadioCard.Item
                                      key={option.id}
                                      value={option.id}
                                    >
                                      {optionValue}
                                      {impact?.unit && (
                                        <span> ({impact.unit})</span>
                                      )}
                                      {(
                                        stage !== "userEstimate" &&
                                        option.weight !== undefined
                                      ) ?
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            fontSize: "0.85em",
                                          }}
                                        >
                                          <Icon
                                            style={{ marginRight: 2 }}
                                            name="level up alternate"
                                            size="small"
                                          />
                                          <div>
                                            Weight: {option.weight || 0}
                                          </div>
                                        </div>
                                      : null}
                                    </RadioCard.Item>
                                  );
                                })}
                                {(
                                  statementDefinition.impacts[impact._id]
                                    .customOptionEnabled ||
                                  rangeInputType === "allowCustom"
                                ) ?
                                  <RadioCard.Item value="__custom">
                                    Other
                                  </RadioCard.Item>
                                : null}
                              </RadioCard.Group>
                            : null}
                            <Form style={{ marginTop: 15 }}>
                              {(
                                impactStatement?.impacts?.[impact._id]?.choice
                                  ?.custom
                              ) ?
                                editable ?
                                  <Form.Field
                                    control={Input}
                                    editable={editable}
                                    label={`Define value${impact?.unit ? ` (${impact.unit})` : ""}`}
                                    type={
                                      (
                                        impact.type === "numerical" ||
                                        impact.type === "money" ||
                                        impact.type === "time"
                                      ) ?
                                        "number"
                                      : "text"
                                    }
                                    required
                                    placeholder="Please provide a custom value"
                                    value={
                                      impactStatement?.impacts?.[impact._id]
                                        ?.choice?.value
                                    }
                                    onChange={(_e, { value }) => {
                                      updateSingleImpact(
                                        impact._id,
                                        (prev) => ({
                                          ...prev,
                                          choice: {
                                            custom: true,
                                            value:
                                              (
                                                impact.type === "numerical" ||
                                                impact.type === "money" ||
                                                impact.type === "time"
                                              ) ?
                                                parseFloat(value)
                                              : value,
                                          },
                                        }),
                                      );
                                    }}
                                  />
                                : <>
                                    <h5>Impact value</h5>
                                    <p>
                                      {
                                        impactStatement?.impacts?.[impact._id]
                                          ?.choice?.value
                                      }
                                    </p>
                                  </>

                              : null}
                              {(
                                statementDefinition.impacts[impact._id]
                                  .frequencyEnabled
                              ) ?
                                editable ?
                                  <Form.Dropdown
                                    selection
                                    label={`${t("common:capitalise", { key: "generic.impact" })} frequency`}
                                    value={
                                      impactStatement?.impacts?.[impact._id]
                                        ?.frequency ?? "one-off"
                                    }
                                    onChange={(_e, { value }) => {
                                      updateSingleImpact(
                                        impact._id,
                                        (prev) => ({
                                          ...prev,
                                          frequency: value,
                                        }),
                                      );
                                    }}
                                    options={[
                                      {
                                        key: "one-off",
                                        value: "one-off",
                                        text: "One-off",
                                      },
                                      {
                                        key: "weekly",
                                        value: "weekly",
                                        text: "Weekly",
                                      },
                                      {
                                        key: "monthly",
                                        value: "monthly",
                                        text: "Monthly",
                                      },
                                      {
                                        key: "annually",
                                        value: "annually",
                                        text: "Annually",
                                      },
                                    ]}
                                  />
                                : <>
                                    <h5>
                                      {t("common:capitalise", {
                                        key: "generic.impact",
                                      })}{" "}
                                      frequency
                                    </h5>
                                    <p>
                                      {
                                        impactStatement?.impacts?.[impact._id]
                                          ?.frequency
                                      }
                                    </p>
                                  </>

                              : null}
                              {/* Show warning if
                              - no choice made OR custom choice selected but no value provided
                              AND impact has valid options available
                              */}
                              {(!impactStatement?.impacts?.[impact._id]
                                ?.choice ||
                                (impactStatement?.impacts?.[impact._id]?.choice
                                  ?.custom === true &&
                                  !impactStatement?.impacts?.[impact._id]
                                    ?.choice?.value)) &&
                                statementDefinition.impacts[impact._id]
                                  .options && (
                                  <Message color="yellow">
                                    <Icon name="exclamation circle" />
                                    Please select the value for this{" "}
                                    {t("generic.impact")} from the options
                                    above.
                                  </Message>
                                )}
                              {editable ?
                                <>
                                  <Message compact info>
                                    <Icon name="exclamation circle" />
                                    Please provide additional context for your
                                    selection. To ensure proper assessment it is
                                    important to provide an explanation of how
                                    this {t("generic.impact")} can be measured
                                    before and after its implementation.
                                  </Message>
                                  <Form.TextArea
                                    label="Details and justification"
                                    editable={editable}
                                    placeholder={`Why did you choose this ${t("generic.impact")}? How does it connect to your ${t("generic.idea")}? How can the ${t("generic.impact")} of your proposal be measured and evaluated?`}
                                    required={
                                      statementDefinition.requireJustification
                                    }
                                    defaultValue={
                                      impactStatement?.impacts?.[impact._id]
                                        ?.justification
                                    }
                                    onChange={(_e, { value }) => {
                                      updateSingleImpact(
                                        impact._id,
                                        (prev) => ({
                                          ...prev,
                                          justification: value,
                                        }),
                                      );
                                    }}
                                  />
                                </>
                              : <>
                                  <h5>Details and justification</h5>
                                  <p>
                                    {
                                      impactStatement?.impacts?.[impact._id]
                                        ?.justification
                                    }
                                  </p>
                                </>
                              }
                            </Form>
                          </div>
                        </CheckboxHeader>
                      </div>
                    </>
                  );
                })}
              </AccordionContent>
            </>
          );
        })}
      </Accordion>
      <Divider hidden />
      {editable ?
        <Form>
          <h4>Additional comments</h4>
          <Form.TextArea
            placeholder={`Provide any additional context, assumptions, or considerations relating to your estimations in this ${t("generic.impact")} statement...`}
            defaultValue={impactStatement?.description}
            onChange={(_e, { value }) => {
              setImpactStatement((prev) => ({ ...prev, description: value }));
            }}
          />
        </Form>
      : impactStatement?.description ?
        <>
          <h5>Additional comments</h5>
          <p>{impactStatement?.description}</p>
        </>
      : null}
    </div>
  );
};

export default ImpactStatementView;
